import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

// ---------------------------------------------------------------------------
// NavDropdown — Wave 2.2 Phase A
//
// Reusable top-nav dropdown. Renders a trigger button with a chevron and an
// absolutely-positioned panel containing menu items. Click-to-open, no hover
// (touch-friendly + accessible). Closes on Esc, click outside, or item click.
//
// Used by DashboardLayout to collapse the previously-flat 25-item merchant
// nav (and 14-item admin / 10-item customer navs) into 4-7 top-level groups.
//
// ARIA:
//   - Trigger button: aria-haspopup="menu", aria-expanded={open}
//   - Panel: role="menu"
//   - Items (via NavDropdownItem): role="menuitem"
//
// Focus management:
//   - Closing via Esc returns focus to the trigger button (keyboard users)
//   - Closing via click-outside does NOT steal focus (mouse users)
//
// Item-click handling:
//   - NavDropdownItem reads the close() callback from context and invokes it
//     on click. Lets us close the panel even when the click doesn't change
//     the URL (e.g. clicking the link to the page you're already on).
//
// Right-edge overflow:
//   - Pass align="right" for dropdowns near the right edge of the nav so the
//     panel opens leftward instead of overflowing the viewport.
// ---------------------------------------------------------------------------

interface NavDropdownContextValue {
  close: () => void;
}

export const NavDropdownContext = createContext<NavDropdownContextValue | null>(null);

interface NavDropdownProps {
  label: string;
  children: React.ReactNode;
  /** Group is "active" when the current route is one of its items. */
  isActive?: boolean;
  /** Panel alignment relative to the trigger. Right-align for dropdowns near the viewport's right edge. */
  align?: 'left' | 'right';
}

const NavDropdown: React.FC<NavDropdownProps> = ({
  label,
  children,
  isActive = false,
  align = 'left',
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef   = useRef<HTMLDivElement>(null);

  const close = useCallback((returnFocus = false) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const toggle = useCallback(() => setOpen((o) => !o), []);

  // Close on click outside the trigger + panel
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target))   return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Esc closes + returns focus to the trigger (keyboard a11y)
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, close]);

  const triggerClassName = [
    'nav-dropdown-trigger',
    isActive && 'nav-dropdown-trigger--active',
    open     && 'nav-dropdown-trigger--open',
  ].filter(Boolean).join(' ');

  const panelClassName = [
    'nav-dropdown-panel',
    align === 'right' && 'nav-dropdown-panel--right',
  ].filter(Boolean).join(' ');

  return (
    <div className="nav-dropdown">
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        {label}
        <span className="nav-dropdown-chevron" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div ref={panelRef} className={panelClassName} role="menu">
          <NavDropdownContext.Provider value={{ close: () => close(false) }}>
            {children}
          </NavDropdownContext.Provider>
        </div>
      )}
    </div>
  );
};

export default NavDropdown;
